/* eslint-disable import/prefer-default-export */
import { parseISO } from "date-fns";
import type {
  CellObject,
  ColInfo,
  ParsingOptions,
  WorkSheet,
  XLSX$Utils,
} from "xlsx";
import { ColumnDefinition } from "utils/xlsxImportExport/XlsxImportColumn";
import { ValidatedRow } from "utils/xlsxImportExport/XlsxImportWorkSheet";

export const DEFAULT_COL_INFO: ColInfo = { wch: 10 };

type ProcessCellContext<TValidatedRow extends ValidatedRow> = {
  columnDef: ColumnDefinition<TValidatedRow>;
  row: TValidatedRow;
};

export function sanitizeSheetName(name: string) {
  return name.replaceAll(/[:\\/?*[\]]/g, "_");
}

export type XlsxExportColumnFormatParams<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TValidatedRow extends ValidatedRow = any,
> = {
  parseValue?: (value: unknown) => unknown;
  columnFormat?: string;
  columnInfo?: ColInfo;
  processCell?: (
    cell: CellObject,
    context: ProcessCellContext<TValidatedRow>,
  ) => void;
};

type FormatWorksheetColumnOptions = {
  numHeaderRows?: number;
};

export function makeFormatWorksheetColumnWithXlsx(xlsxUtils: XLSX$Utils) {
  return (
    worksheet: WorkSheet,
    column: number,
    format: string,
    { numHeaderRows = 1 }: FormatWorksheetColumnOptions = {},
  ) => {
    const wsRef = worksheet["!ref"];
    if (wsRef) {
      // From: https://github.com/SheetJS/sheetjs/issues/885#issuecomment-814794497
      const wsRange = xlsxUtils.decode_range(wsRef);
      for (
        let row = wsRange.s.r + numHeaderRows;
        row <= wsRange.e.r;
        row += 1
      ) {
        const ref = xlsxUtils.encode_cell({ r: row, c: column });
        const cellObj = worksheet[ref] as CellObject;
        if (cellObj && cellObj.t === "n") {
          cellObj.z = format;
        }
      }
    }
  };
}

export async function makeFormatColumn(worksheet: WorkSheet) {
  const { default: xlsx } = await import("xlsx");
  const formatColumn = makeFormatWorksheetColumnWithXlsx(xlsx.utils);
  return (
    column: number,
    format: string,
    options?: FormatWorksheetColumnOptions,
  ) => formatColumn(worksheet, column, format, options);
}

export const TWO_DECIMALS = "#,##0.00";
export const INTEGER = "#,##0";
export const CURRENCY_NO_DECIMALS = "$#,##0";
export const CURRENCY_TWO_DECIMALS = "$#,##0.00";
export const PERCENT_ZERO_DECIMALS = "#,##0%";
export const INCHES = '#,##0.0???" in"';
export const POUNDS = '#,##0.0???" lb"';
export const CUBIC_FEET = '#,##0.0??" ft³"';
export const DATE_TIME = "mmm d, yyyy, h:mm:ss AM/PM";

export function formatByColumnKey<K extends string>(
  worksheetFormatter: (column: number, format: string) => void,
  formatsByKey: Partial<Record<K, string>>,
  columnKeys: K[] | Readonly<K[]>,
) {
  (Object.keys(formatsByKey) as K[]).forEach((k) => {
    worksheetFormatter(columnKeys.indexOf(k), formatsByKey[k] as string);
  });
}

export async function processFileToXlsx(file: File, options?: ParsingOptions) {
  const data = await file.arrayBuffer();

  const { default: xlsx } = await import("xlsx");

  return xlsx.read(data, options);
}

export function onlyStringValue(parseValue: (value: string) => unknown) {
  return function onlyStringParseValue(value: unknown) {
    if (value && typeof value === "string") return parseValue(value);
    return value;
  };
}

export function makeDecimalColumn(
  params: XlsxExportColumnFormatParams,
): XlsxExportColumnFormatParams {
  return {
    parseValue: onlyStringValue(parseFloat),
    ...params,
  };
}

export function makeFormattedDecimalColumn(
  columnFormat: string,
  params?: Omit<XlsxExportColumnFormatParams, "columnFormat">,
) {
  return makeDecimalColumn({ columnFormat, ...params });
}

export const currencyNoDecimalsColumn =
  makeFormattedDecimalColumn(CURRENCY_NO_DECIMALS);

export const currencyTwoDecimalsColumn = makeFormattedDecimalColumn(
  CURRENCY_TWO_DECIMALS,
);

export const percentColumn = makeFormattedDecimalColumn(PERCENT_ZERO_DECIMALS);

export const integerColumn: XlsxExportColumnFormatParams = {
  columnFormat: INTEGER,
};

export const dateColumn: XlsxExportColumnFormatParams = {
  parseValue: onlyStringValue(parseISO),
};
